import { useNavigate } from 'react-router-dom';
import { CloseCircleOutlined, ControlOutlined, EditOutlined, ReloadOutlined } from '@ant-design/icons';
import { Button, Modal, Space, Table, Tag, Typography } from 'antd';

import { FeatureFlags } from '@app/web-components';

import { useEditBundleAccess } from '../forms/rules';
import { BundleDetail } from '../pages';
import { useBundleViewPanel, useEligibilityColumn } from '../panels';

export type DetailModalProps = {
  bundle: BundleDetail | undefined;
  onClose: () => void;
};

export const DetailModal = ({ bundle, onClose }: DetailModalProps) => {
  const navigate = useNavigate();
  const editAccess = useEditBundleAccess();
  const { getAccessTableProps, getPanelSpaceProps, refetch: refetchBundle } = useBundleViewPanel(bundle?.id);
  const { addColumn, refetch: refetchEligibility } = useEligibilityColumn(bundle?.id);
  const { isFeatureEnabled } = FeatureFlags.useFeatureFlags();

  const refetch = () => {
    refetchBundle();
    refetchEligibility();
  };

  const { columns: accessColumns, ...accessTableProps } = getAccessTableProps();
  addColumn(accessColumns);

  if (isFeatureEnabled('eager_anchor')) {
    accessColumns?.unshift({
      dataIndex: 'isEagerAnchor',
      title: 'Anchor',
      render: (value: boolean) => (value ? <Tag>Anchor</Tag> : ''),
    });
  }

  return (
    <Modal
      title={
        <Typography.Title level={5} style={{ margin: 0 }}>
          Bundle: {bundle?.name}
        </Typography.Title>
      }
      centered
      width={1100}
      styles={{
        body: {
          paddingTop: 15,
          height: 600,
          overflow: 'auto',
        },
      }}
      open={!!bundle}
      onCancel={onClose}
      footer={[
        <Button key="refresh" onClick={refetch} icon={<ReloadOutlined />}>
          Refresh
        </Button>,
        editAccess.hasBundleOwnership() ? (
          <Button icon={<EditOutlined />} key="edit" onClick={() => navigate(`./${bundle?.id}/edit`)}>
            Edit
          </Button>
        ) : undefined,
        editAccess.hasBundleOwnership() ? (
          <Button icon={<ControlOutlined />} key="manage" onClick={() => navigate(`./${bundle?.id}/manage`)}>
            Manage
          </Button>
        ) : undefined,
        <Button key="close" onClick={onClose} icon={<CloseCircleOutlined />} type="primary">
          Close
        </Button>,
      ]}
    >
      <Space
        {...getPanelSpaceProps({
          children: <Table {...accessTableProps} columns={accessColumns} />,
        })}
      />
    </Modal>
  );
};
